import { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react'
import { FormControl, FormControlLabel, Radio, RadioGroup, Box, CircularProgress, Typography } from '@mui/material'
import { IFilterParams, IDoesFilterPassParams } from 'ag-grid-community'
import ThemeButton from 'ui/components/buttons/theme/Button'
import theme from 'theme'

interface BooleanFilterParams extends IFilterParams {
  getLookup?: (columnId: string) => Promise<string[]>
  columnName?: string
  /* Set by the grid's reactive filter wrapper; undefined when rendered outside a grid. */
  model?: { value?: string } | null
}

export const BooleanFilter = forwardRef<any, BooleanFilterParams>((props, ref) => {
  const [value, setValue] = useState<string>('')
  const [appliedValue, setAppliedValue] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [hasValues, setHasValues] = useState<boolean>(true)
  const appliedValueRef = useRef<string>('')
  const containerRef = useRef<HTMLDivElement>(null)

  // Reset value when clicking outside the filter (closing the popup)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Clicked outside - reset to applied value
        setValue(appliedValueRef.current)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const fetchValues = async () => {
      if (props.getLookup && props.columnName) {
        try {
          setLoading(true)
          const values = await props.getLookup(props.columnName)
          setHasValues(values && values.length > 0)
        } catch (error) {
          console.error('Error fetching boolean filter values:', error)
          setHasValues(false)
        } finally {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }

    fetchValues()
  }, [props.getLookup, props.columnName])

  /* Reactive grids never call setModel, so a model set from outside, like Reset all, arrives only as this prop. */
  useEffect(() => {
    if (props.model === undefined) return

    const next = props.model?.value ?? ''

    setValue(next)
    setAppliedValue(next)
    appliedValueRef.current = next
  }, [props.model])

  useImperativeHandle(
    ref,
    () => ({
      doesFilterPass: (params: IDoesFilterPassParams): boolean => {
        if (!appliedValue) return true
        const cellValue = params.data[props.column.getColId()]
        if (appliedValue === 'true') return cellValue === true || cellValue === 'true'
        if (appliedValue === 'false') return cellValue === false || cellValue === 'false'
        return true
      },

      getModel: () => {
        // Use ref instead of state to avoid race condition when AG Grid calls getModel
        // immediately after setModel (before React state updates)
        if (!appliedValueRef.current) return null
        return { filterType: 'boolean', value: appliedValueRef.current }
      },

      setModel: (model: any) => {
        const newValue = model ? model.value : ''
        setValue(newValue)
        setAppliedValue(newValue)
        appliedValueRef.current = newValue
      },

      isFilterActive: () => {
        // Use ref instead of state for consistency with getModel
        return !!appliedValueRef.current
      },

      onNewRowsLoaded: () => {
        //
      },
      onAnyFilterChanged: () => {
        //
      },
      afterGuiAttached: () => {
        // Reset selection to match the applied value when popup opens (using ref to avoid stale closure)
        setValue(appliedValueRef.current)
      },
      getModelAsString: () => {
        return appliedValue ? `${props.column.getColId()}: ${appliedValue === 'true' ? 'Yes' : 'No'}` : ''
      }
    }),
    [appliedValue, props.column]
  )

  const handleApply = () => {
    // Update applied value and set the filter model in AG Grid's state
    setAppliedValue(value)
    appliedValueRef.current = value

    if (props.api && props.column) {
      const columnId = props.column.getColId()

      // Get current filter model
      const currentModel = props.api.getFilterModel() || {}

      // Update with our filter
      if (value) {
        currentModel[columnId] = { filterType: 'boolean', value }
      } else {
        delete currentModel[columnId]
      }

      // Set the updated model
      props.api.setFilterModel(currentModel)

      // Close the filter popup by dispatching Escape key event
      const escapeEvent = new KeyboardEvent('keydown', {
        key: 'Escape',
        keyCode: 27,
        bubbles: true,
        cancelable: true
      })
      document.dispatchEvent(escapeEvent)
    }
  }

  const handleClear = () => {
    setValue('')
    setAppliedValue('')
    appliedValueRef.current = ''

    if (props.api && props.column) {
      const columnId = props.column.getColId()
      const currentModel = props.api.getFilterModel() || {}
      delete currentModel[columnId]

      props.api.setFilterModel(currentModel)
    }
  }

  const handleChange = (event: any, newValue: string) => {
    setValue(newValue)
  }

  if (loading) {
    return (
      <Box
        sx={{
          p: 0,
          minWidth: 244,
          backgroundColor: theme.colors.devicesPopupBg,
          border: 0,
          borderRadius: theme.attributes.borderRadius.secondary,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px',
          minHeight: 60,
          paddingLeft: '16px'
        }}
      >
        <CircularProgress size={16} thickness={4} sx={{ color: theme.colors.foreground }} />
        <Typography
          sx={{
            color: theme.colors.foreground,
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: '14px'
          }}
        >
          Loading...
        </Typography>
      </Box>
    )
  }
  if (!hasValues) {
    return (
      <Box
        sx={{
          p: 0,
          minWidth: 244,
          backgroundColor: theme.colors.devicesPopupBg,
          border: 0,
          borderRadius: theme.attributes.borderRadius.secondary
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            padding: '16px',
            color: '#938F99',
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: '14px'
          }}
        >
          No values for filter
        </Typography>
      </Box>
    )
  }
  return (
    <Box
      ref={containerRef}
      sx={{
        p: 2,
        minWidth: 244,
        backgroundColor: theme.colors.devicesPopupBg,
        border: 0,
        borderRadius: theme.attributes.borderRadius.secondary
      }}
    >
      <FormControl component="fieldset" fullWidth>
        <RadioGroup value={value} onChange={handleChange} sx={{ gap: '8px' }}>
          <FormControlLabel
            value=""
            control={
              <Radio
                size="small"
                sx={{
                  color: theme.colors.devicesSecondaryText,
                  '&.Mui-checked': { color: theme.colors.menuItemText },
                  '& .MuiSvgIcon-root': { fontSize: theme.typography.fontSize.xLarge }
                }}
              />
            }
            label="All"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                color: theme.colors.devicesSecondaryText,
                fontSize: theme.typography.fontSize.large,
                fontFamily: theme.typography.fontFamily.primary,
                fontStyle: 'normal',
                fontWeight: theme.typography.fontWeight.bold,
                lineHeight: '22px'
              }
            }}
          />
          <FormControlLabel
            value="true"
            control={
              <Radio
                size="small"
                sx={{
                  color: theme.colors.devicesSecondaryText,
                  '&.Mui-checked': { color: theme.colors.menuItemText },
                  '& .MuiSvgIcon-root': { fontSize: theme.typography.fontSize.xLarge }
                }}
              />
            }
            label="true"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                color: theme.colors.devicesSecondaryText,
                fontSize: theme.typography.fontSize.large,
                fontFamily: theme.typography.fontFamily.primary,
                fontStyle: 'normal',
                fontWeight: theme.typography.fontWeight.bold,
                lineHeight: '22px'
              }
            }}
          />
          <FormControlLabel
            value="false"
            control={
              <Radio
                size="small"
                sx={{
                  color: theme.colors.devicesSecondaryText,
                  '&.Mui-checked': { color: theme.colors.menuItemText },
                  '& .MuiSvgIcon-root': { fontSize: theme.typography.fontSize.xLarge }
                }}
              />
            }
            label="false"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                color: theme.colors.devicesSecondaryText,
                fontSize: theme.typography.fontSize.large,
                fontFamily: theme.typography.fontFamily.primary,
                fontStyle: 'normal',
                fontWeight: theme.typography.fontWeight.bold,
                lineHeight: '22px'
              }
            }}
          />
        </RadioGroup>
      </FormControl>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: `${theme.spacing.xSmall}px`
        }}
      >
        <ThemeButton size="small" themevariant="ghost" onClick={handleApply}>
          Apply
        </ThemeButton>
        <ThemeButton size="small" themevariant="ghost" onClick={handleClear}>
          Reset
        </ThemeButton>
      </Box>
    </Box>
  )
})
